import type { EnrollmentStatus, TermState } from '@repo/shared-types';

/**
 * What the enrollment card says about a khoá, as one word.
 *
 * `null` means the enrollment has no khoá at all (MONTHLY_FLAT, or
 * PER_SESSION billed MONTHLY) — those carry `termState: null` and must show no
 * khoá badge, no consequence line and no Gia hạn button.
 */
export type EnrollmentKhoaState = 'out' | 'nearlyOut' | 'normal' | 'paused' | 'ended' | null;

/** The two fields the rule reads, so callers can pass an `EnrollmentWithSubject`
 *  straight through without the pure function depending on pricing/slots/dates. */
export interface KhoaRow {
  status: EnrollmentStatus;
  termState: TermState | null;
}

export interface EnrollmentKhoa {
  state: EnrollmentKhoaState;
  /** Sessions left in the open khoá; 0 when there is no open khoá to count.
   *  May be NEGATIVE on an over-run — `TermState.remaining` is deliberately
   *  unclamped — but that negative number is never actually shown: the 'out'
   *  badge/consequence copy below carries no `{{count}}`. It only feeds the
   *  `remaining <= 0` classification above; 'nearlyOut' is the only state
   *  whose `remaining` (always 1 or 2, by the branch order below) reaches a
   *  template. */
  remaining: number;
}

/**
 * Classifies one enrollment for the detail card (design 13 §4).
 *
 * The thresholds are `termNudge.ts`'s, on purpose: `remaining <= 0` is out,
 * `0 < remaining <= 2` is nearly out. The workbench "Cần chú ý" card and this
 * card must never disagree about who is running out, so the numbers live in one
 * sentence in each file and a test asserts they agree.
 *
 * Two deliberate differences from `termNudgeState`, both because this renders a
 * single badge on a single card rather than a nudge list:
 *
 *  1. PAUSED and ENDED win over the khoá. `termNudgeState` counts a PAUSED
 *     student's spent khoá (a paused student resumes under the same block, and
 *     the workbench must not go silent about the money). Here the card has one
 *     badge slot, and "Tạm dừng" is the fact that explains why no sessions are
 *     being consumed — so it takes the slot. Nothing goes silent: the same
 *     student still appears in "Cần chú ý" via `termNudgeState`.
 *  2. `hasOpenTerm: false` is `'normal'`, not `'out'`. `computeTermState`
 *     reports `remaining: 0` as a placeholder when no term has started; treating
 *     that as spent would demand payment from a student who has not begun.
 *     'normal' is also what the card wants — the button there reads
 *     "Bắt đầu khoá", not "Gia hạn khoá".
 */
export function enrollmentKhoaState(row: KhoaRow): EnrollmentKhoa {
  if (row.status === 'ENDED') return { state: 'ended', remaining: 0 };
  if (row.status === 'PAUSED') return { state: 'paused', remaining: 0 };
  if (!row.termState) return { state: null, remaining: 0 };
  if (!row.termState.hasOpenTerm) return { state: 'normal', remaining: 0 };

  const remaining = row.termState.remaining;
  if (remaining <= 0) return { state: 'out', remaining };
  if (remaining <= 2) return { state: 'nearlyOut', remaining };
  return { state: 'normal', remaining };
}
